/* eslint-disable react/no-array-index-key */
import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {usePrevious} from '../../../../../../../srcCommon/helpers/hookHelpers';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {isIntegrationSettingFieldLoading} from '../../../../../../helpers/integrationHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  ShopifyIntegrationModel,
  ShopifyIntegrationSettingsModel,
  ShopifyIntegrationSettingsStoreModel,
} from '../../../../../../models/api/integrationModel';
import {decryptIntegrationSettingsAsyncAction} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {SettingsAppsConfigureShopifyStore} from './settingsAppsConfigureShopifyStore';

/*
 * Constants.
 */

const seeHowItWorksLink = 'http://help.frontapp.com/t/m26jxk/how-to-enable-and-use-the-shopify-integration';

/*
 * Props.
 */

interface SettingsAppsConfigureShopifyProps {
  integration?: ShopifyIntegrationModel;
  configurationSettings?: ShopifyIntegrationSettingsModel;
  onIsSaveEnabled: HandlerOf<boolean>;
  setConfigurationSettings: HandlerOf<ShopifyIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledStoreWrapperDiv = styled.div`
  overflow: auto;
  max-height: 500px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureShopify: FC<SettingsAppsConfigureShopifyProps> = (props) => {
  const {
    configurationSettings = {stores: []},
    integration,
    setConfigurationSettings,
    onIsSaveEnabled,
  } = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const storeWrapperRef = useRef<HTMLDivElement>(null);
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const stores = configurationSettings.stores || [
    {
      apiDomain: '',
      apiPassword: '',
      apiKey: '',
    },
  ];
  const previousStores = usePrevious(stores);
  const isDecrypting = computeIsDecrypting(stores);

  // When we first load the settings, we may need to decrypt the settings.
  useInitialEffect(() => {
    if (!integration) {
      return;
    }
    dispatch(decryptIntegrationSettingsAsyncAction(integration));
  });

  // Watches the stores and if a new one is added we will scroll to the bottom of the wrapper.
  useEffect(() => {
    if (
      previousStores &&
      configurationSettings.stores &&
      configurationSettings.stores.length > previousStores.length &&
      storeWrapperRef.current
    ) {
      storeWrapperRef.current.scrollTo({
        top: storeWrapperRef.current.scrollHeight,
      });
    }
  }, [configurationSettings.stores, previousStores]);

  // Controls if save is enabled for this dialog.
  //  This requires the api-key to be filled out, but there is no validation.
  useEffect(() => {
    if (
      !configurationSettings ||
      !configurationSettings.stores ||
      configurationSettings.stores.length === 0 ||
      isDecrypting
    ) {
      onIsSaveEnabled(false);
      return;
    }
    onIsSaveEnabled(
      !configurationSettings.stores.some(
        (store) => store.apiDomain === '' || store.apiKey === '' || store.apiPassword === '',
      ),
    );
  }, [configurationSettings, isDecrypting, onIsSaveEnabled]);

  const onAddStore = () => {
    setConfigurationSettings({
      ...configurationSettings,
      stores: [
        ...stores,
        {
          apiDomain: '',
          apiKey: '',
          apiPassword: '',
        },
      ],
    });
    if (storeWrapperRef.current) {
      storeWrapperRef.current.scrollTo({
        top: storeWrapperRef.current.scrollHeight,
      });
    }
  };

  const onRemoveStore = (index: number) => {
    setConfigurationSettings({
      ...configurationSettings,
      stores: [...stores.filter((_s, i) => i !== index)],
    });
  };

  const onUpdateStore = (store: ShopifyIntegrationSettingsStoreModel, index: number) => {
    setConfigurationSettings({
      ...configurationSettings,
      stores: [
        ...stores.map((s, i) => {
          if (i === index) {
            return store;
          }
          return s;
        }),
      ],
    });
  };

  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-shopify-description">
        Display customer information &amp; orders, issue refunds.
      </Trans>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-shopify-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      <StyledStoreWrapperDiv ref={storeWrapperRef}>
        {stores.map((store, index) => (
          <SettingsAppsConfigureShopifyStore
            key={`store-${index}`}
            store={store}
            index={index}
            shouldAllowRemovingStore={stores.length > 1}
            isDecrypting={isDecrypting}
            onRemoveStore={onRemoveStore}
            onUpdateStore={onUpdateStore}
          />
        ))}
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onAddStore} isDisabled={!isGlobalAdmin}>
            <Trans id="settings-apps-configure-app-dialog-shopify-add-new-store">Add new store</Trans>
          </Button>
          <ButtonGroupSpacer />
        </ButtonGroup>
      </StyledStoreWrapperDiv>
    </>
  );
};

/*
 * Helpers.
 */

function computeIsDecrypting(stores: ReadonlyArray<ShopifyIntegrationSettingsStoreModel>) {
  // If any store has a field that is considered loading, we are decrypting them.
  return stores.some(
    (store) =>
      isIntegrationSettingFieldLoading(store.apiKey || '') ||
      isIntegrationSettingFieldLoading(store.apiPassword || ''),
  );
}
